// Board — today and tomorrow by crew, or the whole week by crew member.
//
// GET /api/dispatch/board?date=; a move is PATCH /api/jobs/:id/schedule
// carrying only the fields that changed (lib/format.scheduleChanges).
//
// The Week tab is the rebuild's home for the classic CrewWeekView
// (docs/REBUILD_PARITY.md, Schedule). It answers "who is free this week",
// which the two-day board cannot: the board is grouped by LEAD and returns
// exactly two days per request, so a week is four requests re-bucketed by crew
// member. All of that — including what a HALF-answered week is allowed to say
// — lives in lib/crewWeek.js, checked against the route file by
// dispatchCrewWeek.test.js.
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import {
  JobCard, Pill, SectionLabel, Sheet, Notice, Skeleton, EmptyState, FwButton,
  FwField, FwSelect, FwInput, FwTextarea, usePhoneLayout, formatHours,
} from '../../../ui/fw';
import { crewLabel, jobCardBadges, jobCardTone, statusLabel } from '../../shared/jobFields';
import { get, patch, describeError } from '../../shared/http';
import { addDaysISO } from '../../../utils/scheduleDates';
import { usePageChrome } from '../../shell/chrome';
import { jobRecordHref } from '../../shared/jobLink';
import { useCan } from '../../../hooks/usePermission';
import { dayLong, dayShort, relativeDayLabel, weekOfLabel, scheduleChanges, serverSentence, spanLabel, clockTime, MOVE_STATUSES } from '../lib/format';
// The status vocabulary, the tone map, the badge set and the crew sentence —
// declared once in apps/shared and imported, never restated here. Five copies
// of the tone map is what produced the active/completed colour inversion this
// module's own header used to describe (v2.9.5).
import './Board.css';
import {
  boardApiPath, approveApiPath, weekDays, boardAnchors, mergeBoardDays, crewWeekRows,
  dayJobCount, weekWarning, chipMeta, UNASSIGNED_ROW,
} from '../lib/crewWeek';

const list = (a) => (Array.isArray(a) ? a : []);

/**
 * WHERE the job is — the client AND the property, in that order.
 *
 * This was `j.property_name || j.client_name`, so a job with a property never
 * named its client at all. Measured in Chromium at 320px on 2026-09-11
 * against a seeded board, every card read "18 Cedar Ridge Road" and no card
 * anywhere on the screen said whose it was. A dispatcher moving work between
 * crews is asked "whose place is that" constantly, and the answer was one the
 * board already had and did not print.
 *
 * Both, joined, when both exist; whichever exists when only one does; and ''
 * when neither, so JobCard drops the line rather than drawing an empty one.
 * The `no_client` badge is what says so in that case — a word, not a blank.
 */
function jobPlace(j) {
  return [j.client_name, j.property_name].filter(Boolean).join(' · ');
}

/**
 * WHO IS ON IT RIGHT NOW — the one fact the board could not state.
 *
 * `on_clock` is GET /api/dispatch/board's copy of the open `time_entries`
 * rows, built by the SAME function that answers GET /api/dispatch/now
 * (server/routes/dispatch.js `liveShifts`), so the two dispatch screens
 * cannot disagree about who is working. Before 2026-09-12 the board had no
 * idea: a dispatcher asking "how is today going" had to open /now, remember
 * three names, and come back.
 *
 * The TIME, not an elapsed count: this screen has no ticking clock and does
 * not poll, so a "1h 12m" rendered once would sit there going stale and
 * reading as a live figure. "clocked in 9:12" is true whenever it is read.
 * The elapsed version belongs on /now, which ticks it (Now.js `BigClock`).
 *
 * A whole crew is "Marcus Reyes +1", the same shortening `crewSummary` uses
 * for a roster, because three names in a fact slot wrap to three lines at
 * 320px and the card is about the JOB.
 */
function liveFact(j) {
  const on = list(j && j.on_clock);
  if (!on.length) return '';
  const first = on[0] || {};
  if (!first.name) return '';               // the badge already says somebody is
  const who = on.length === 1 ? first.name : `${first.name} +${on.length - 1}`;
  const at = clockTime(first.started_at);
  return at ? `${who} clocked in ${at}` : `${who} on the clock`;
}

/**
 * The crew — but ONLY when it says something the heading above does not.
 *
 * Measured at 390px on 2026-09-12: under a section headed ANA SILVA, every
 * card's fact line read "Ana Silva crew". A quarter of each card's text was
 * the heading, restated, on every row, and the facts that DIFFER between rows
 * were pushed out of a line that only holds two or three items on a phone.
 *
 * So the rule is: a slot is spent on what differs. Under a crew heading, a job
 * carried by exactly that lead says nothing — the heading said it. A job
 * carrying MORE people than the lead ("Marcus Reyes +1") does differ from the
 * row above it and keeps the line. In the Unassigned rail there is no lead
 * name to repeat, and "Unassigned" on the card itself is what survives
 * scrolling past the heading, so it is always kept.
 */
function crewFact(j, leadName) {
  const label = crewLabel(j, { fallbackLead: leadName });
  if (!leadName) return label;
  return label === `${leadName} crew` ? '' : label;
}

/**
 * The short facts under the place. Each is dropped when it has nothing to say
 * — never a dash. An em dash in a fact slot is a slot spent saying "no
 * answer", and docs/APP_REBUILD.md is explicit that the fix is to drop the
 * slot and show a real fact instead.
 *
 * Order is what a dispatcher scans for: what is happening NOW, then who (when
 * that is not already on the heading), then where the job is in its own run,
 * then the numbers.
 */
function jobFacts(j, day, leadName) {
  return [
    liveFact(j),
    crewFact(j, leadName),
    spanLabel(j, day),
    // Truthiness, NOT `!= null`. A 0 here is either the column's DEFAULT —
    // no estimate — or a real estimate so short it rounded away, and both
    // printed "0 h est" on a job that a dispatcher can see has time on it.
    // Field discarded it and Dispatch did not (field/screens/Job.js,
    // field/jobCardProps.js). The server stopped sending the rounded 0 on
    // 2026-09-14 (server/utils/estimatedTime.js), and this guard is the
    // screen's own half: a cached read replayed by the offline queue, or an
    // older server during a rolling deploy, can still hand this card a 0.
    j.estimated_hours ? `${formatHours(j.estimated_hours)} h est` : '',
    j.subtask_total ? `${j.subtask_done || 0} of ${j.subtask_total} done` : '',
  ].filter(Boolean);
}

/** Every job drawn under one day's heading, crews and the unassigned rail. */
function dayJobs(d) {
  return [...list(d && d.crews).flatMap((c) => list(c.jobs)), ...list(d && d.unassigned)];
}

/**
 * Is every job on this day in the SAME state?
 *
 * If it is, the status pill on each card is true of every row on the screen,
 * which makes it decoration rather than information — the owner's twelve amber
 * ACTIVE pills, measured 2026-09-12. The fact is not thrown away: `daySummary`
 * says it once, on the day.
 *
 * A day holding ONE job is never uniform by this test. There is no other row
 * for its pill to repeat, and a lone card with no state at all is the opposite
 * defect (docs/APP_REBUILD.md §3.3: status is never optional).
 */
function dayStatusIsUniform(jobs) {
  if (jobs.length < 2) return false;
  return new Set(jobs.map((j) => j.status || 'todo')).size === 1;
}

/**
 * The day in one line, beside its own heading: how much work, how much of it
 * is moving, how much is behind, and — when they are all alike — what state
 * they are all in.
 *
 * This is where the suppressed status pill goes, and it costs no vertical
 * room: it sits on the heading row the day already had.
 */
function daySummary(jobs) {
  if (!jobs.length) return '';
  const parts = [`${jobs.length} ${jobs.length === 1 ? 'job' : 'jobs'}`];
  const live = jobs.filter((j) => list(j.on_clock).length > 0).length;
  if (live) parts.push(`${live} on the clock`);
  const late = jobs.filter((j) => j.late).length;
  if (late) parts.push(`${late} late`);
  if (dayStatusIsUniform(jobs)) parts.push(`all ${statusLabel(jobs[0].status).toLowerCase()}`);
  return parts.join(' · ');
}

/**
 * The badges on a board card: the shared set, plus the one fact only this
 * payload can state.
 *
 * ── Why Unassigned is added HERE and not taken from jobBadges ───────────────
 *
 * `jobBadges` (apps/shared/jobFields.js) is the vocabulary every screen
 * shares: the status, No client, Blocked. It cannot answer "has anybody got
 * this job", because the field that says so is named differently in each
 * payload — `lead_user_id` here (server/routes/dispatch.js shapeJob),
 * `user_id` in the Office jobs row. This composes onto that set rather than
 * starting a second vocabulary beside it.
 *
 * ── And why the name of the crew is NOT on the card ─────────────────────────
 *
 * The obvious move is `crewSummary(job)`, which the Office jobs list and the
 * Field card both print. It reads `job.assignees`, and on THIS screen that
 * array can disagree with the grouping around it:
 * `PATCH /api/jobs/:id/schedule` — the write this screen's own move sheet
 * sends — updates `jobs.user_id` and never touches `job_assignees`
 * (server/routes/jobs.js:1322-1331; `PUT /:id` at :1026 does call
 * `crew.setJobRoster`, which is what makes the omission visible). So after a
 * reassignment from this board the card would sit under Marcus's heading and
 * name Ana, because the roster still holds the old crew.
 *
 * `lead_user_id` is `jobs.user_id`, which is the column the server groups
 * this board by (`if (!r.user_id) unassigned.push(...)`), so a badge read off
 * it can never disagree with the section the card is in. Naming a crew is
 * worth doing and needs the server bug fixed first; stating "Unassigned"
 * only when the grouping already says so is worth doing now and cannot be
 * wrong. A plausible-looking wrong name is the outcome CLAUDE.md calls the
 * worst available here.
 */
function boardBadges(j, { sameStatusAllDay = false } = {}) {
  // ── A pill is for what is EXCEPTIONAL about this row (2026-09-12) ────────
  //
  // Measured at 390px: twelve jobs, twelve amber ACTIVE pills. A badge that is
  // true of every row on the screen tells a dispatcher nothing and teaches
  // them to stop reading the pills — including the two that matter. So the
  // STAGE is dropped when the whole day shares it (`dayStatusIsUniform`, said
  // once on the day's heading instead), and kept the moment one job differs.
  //
  // The two added here are exceptional by definition and are never dropped:
  // LATE (the job's last scheduled day has passed and it is unfinished —
  // server/routes/dispatch.js isJobLate) and ON THE CLOCK (somebody is
  // standing on it right now). Both are read straight off the payload rather
  // than worked out here: a screen that decides for itself what "late" means
  // is the second implementation this whole change exists to remove.
  //
  // `jobCardBadges`, not `jobBadges`: it is the shared set PLUS the
  // boolean-only blocked case, which is what the Field offline cache
  // carries. Wrapping the shared helper is what keeps Dispatch from
  // growing a third vocabulary for the same five statuses.
  // Unassigned is NOT added here. It used to be, while the facts line could
  // not be trusted to carry a crew — now that it can, `crewLabel` names the
  // person when there is one and says "Unassigned" when there is not, and a
  // badge repeating the second half would make the card say it twice and the
  // two halves drift apart the first time one of them is edited.
  const out = [];
  if (j && j.late) out.push({ key: 'late', label: 'Late', tone: 'stop' });
  if (list(j && j.on_clock).length > 0) out.push({ key: 'on_clock', label: 'On the clock', tone: 'now' });
  for (const b of jobCardBadges(j)) {
    if (b.key === 'status' && sameStatusAllDay) continue;
    out.push(b);
  }
  return out;
}

/**
 * The card's left border colour.
 *
 * `statusTone` is THE map (apps/shared/jobFields.js) and this screen used to
 * carry its own: completed → ok, no_client → warn, everything else → neutral.
 * That fourth branch swallowed todo, active and paused — three of the five
 * real statuses — into one grey, which is the owner's "statuses
 * indistinguishable" stated in code. Blocked still wins, because it is the one
 * state that is not a stage and `stop` is reserved for it.
 *
 * The colour now agrees with the badge beside it, because both come from the
 * same map. A border saying one thing while a pill says another is worse than
 * either alone.
 */
// `jobCardTone` (apps/shared/jobFields.js) is this function, plus the
// `blocked_reason`-without-`blocked` row the Field cache produces. Kept as a
// named re-export so the comment above stays attached to the decision.
const jobTone = jobCardTone;

function JobList({ jobs, day, onPick, leadName = '', sameStatusAllDay = false }) {
  return (
    <div className="dp-cards">
      {jobs.map((j) => (
        <JobCard
          key={j.id}
          /*
            The time column is EMPTY unless the server gave a real one.

            It was `dayShort(day)` — "Fri 11" — under a heading that already
            says "Today · Fri 11 Sep", on every card on that day. `time_label`
            is null for every board row today (server/routes/dispatch.js
            shapeJob), so that column printed the date the reader had just
            read, three times over, in 19px bold.

            `.fw-jcard__time:empty { display: none }` (JobCard.css) collapses
            the column when it has nothing, so the title gets the width back —
            which at 320px is the whole argument. Where the job spans days,
            that fact is in `facts` as "Day 2 of 3", which the heading cannot
            tell you.
          */
          time={j.time_label || ''}
          title={j.title}
          badges={boardBadges(j, { sameStatusAllDay })}
          meta={jobPlace(j)}
          facts={jobFacts(j, day, leadName)}
          tone={jobTone(j)}
          onClick={() => onPick(j)}
          data-job-id={j.id}
        />
      ))}
    </div>
  );
}

function MoveSheet({ job, days, today, leads, onClose, onSaved }) {
  const navigate = useNavigate();
  // The job itself, one tap from the board (Schedule's sheet has the same) —
  // with job oversight only. The board is company-wide, and the Jobs screen
  // finds the record in GET /api/jobs, which answers anyone without oversight
  // with their own and unassigned jobs: a lead granted timesheets.approve could
  // tap another crew's job and land on a list without it (review of #738).
  const allJobs = useCan('jobs', 'manage_all');
  const teamJobs = useCan('jobs', 'manage_team');
  const recordHref = allJobs || teamJobs ? jobRecordHref(job.id) : '';
  const dayDates = days.map((d) => d.date);
  const initialDay = job.scheduled_date && dayDates.includes(job.scheduled_date) ? job.scheduled_date : (job.scheduled_date ? 'other' : dayDates[0] || 'other');
  const [dayChoice, setDayChoice] = useState(initialDay);
  const [otherDate, setOtherDate] = useState(job.scheduled_date || '');
  const [userId, setUserId] = useState(job.lead_user_id == null ? '' : String(job.lead_user_id));
  const [status, setStatus] = useState(job.status || 'active');
  const [reason, setReason] = useState(job.blocked_reason || '');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const statuses = MOVE_STATUSES.some((s) => s.value === job.status) || !job.status
    ? MOVE_STATUSES
    : [{ value: job.status, label: job.status }, ...MOVE_STATUSES];

  const save = async () => {
    const scheduled_date = dayChoice === 'other' ? otherDate : dayChoice;
    const changes = scheduleChanges(job, { scheduled_date, user_id: userId, status, blocked_reason: reason });
    if (Object.keys(changes).length === 0) { onClose(); return; }
    setBusy(true);
    setError(null);
    try {
      await patch(`/jobs/${job.id}/schedule`, changes);
      onSaved();
    } catch (e) {
      setError(serverSentence(e));
      setBusy(false);
    }
  };

  return (
    <Sheet
      open
      title="Move job"
      onClose={onClose}
      footer={<FwButton full size="lg" loading={busy} onClick={save}>Save</FwButton>}
    >
      <div className="dp-sheet-body">
        <div className="dp-quiet">{job.title}{job.property_name ? ` · ${job.property_name}` : ''}</div>
        {recordHref ? (
          <FwButton variant="quiet" icon="arrowRight" onClick={() => navigate(recordHref)} disabled={busy} data-action="open-job">Open job</FwButton>
        ) : null}
        {error ? <Notice tone="stop">{error}</Notice> : null}
        <FwField label="Day" htmlFor="dp-move-day">
          <FwSelect value={dayChoice} onChange={(e) => setDayChoice(e.target.value)}>
            {days.map((d) => <option key={d.date} value={d.date}>{relativeDayLabel(d.date, today)} · {dayLong(d.date)}</option>)}
            <option value="other">Another date</option>
          </FwSelect>
        </FwField>
        {dayChoice === 'other' ? (
          <FwField label="Date" htmlFor="dp-move-date">
            <FwInput type="date" value={otherDate} onChange={(e) => setOtherDate(e.target.value)} />
          </FwField>
        ) : null}
        <FwField label="Crew lead" htmlFor="dp-move-lead">
          <FwSelect value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">Unassigned</option>
            {leads.map((l) => <option key={l.user_id} value={String(l.user_id)}>{l.name}</option>)}
          </FwSelect>
        </FwField>
        <FwField label="Status" htmlFor="dp-move-status">
          <FwSelect value={status} onChange={(e) => setStatus(e.target.value)}>
            {statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </FwSelect>
        </FwField>
        <FwField label="Blocked reason" hint="Leave empty to clear a block." htmlFor="dp-move-reason">
          <FwTextarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </FwField>
      </div>
    </Sheet>
  );
}

/**
 * The whole week, one row per active crew member plus Unassigned.
 *
 * Four board requests (two days each) and one approve request for the week
 * boundary and the roster. A request that does not come back costs its two
 * days: those columns say so rather than drawing an empty cell, because
 * "nobody is on Thursday" and "we could not ask about Thursday" are opposite
 * instructions to a dispatcher and look the same in a blank grid.
 */
function CrewWeek({ phone }) {
  const qc = useQueryClient();
  const [week, setWeek] = useState(null);
  const [picked, setPicked] = useState(null);
  const [moving, setMoving] = useState(null);

  // The company's week boundary is a server setting (startOfWeek), so the week
  // is not computed here — it is read off the endpoint that already aligns to
  // it, which is also the only list of who is on the crew.
  const roster = useQuery({
    queryKey: ['dispatch', 'approve', week],
    queryFn: () => get(approveApiPath(week)),
  });
  const start = roster.data && roster.data.start_date;
  const days = useMemo(() => weekDays(start), [start]);
  const anchors = useMemo(() => boardAnchors(start), [start]);

  const boards = useQueries({
    queries: anchors.map((a) => ({
      queryKey: ['dispatch', 'board', a],
      queryFn: () => get(boardApiPath(a)),
      enabled: !!start,
    })),
  });

  const loading = roster.isPending || (!!start && boards.some((b) => b.isPending));
  const answers = boards.map((b) => ({ ok: !b.isError && !!b.data, data: b.data, error: b.error }));
  const merged = mergeBoardDays(answers, days);
  const rows = useMemo(
    () => crewWeekRows({ workers: (roster.data && roster.data.workers) || [], days, byDate: merged.byDate, missing: merged.missing }),
    // merged is rebuilt every render from the query results; depending on its
    // identity would re-run this on every render anyway, so the inputs that
    // actually change the answer are named.
    [roster.data, days, merged.byDate, merged.missing], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const today = merged.today || (roster.data && roster.data.today) || null;
  const activeDay = picked && days.includes(picked) ? picked : (days.includes(today) ? today : days[0]);
  const firstBoardError = boards.find((b) => b.isError);
  const warning = loading ? null : weekWarning({
    missing: merged.missing,
    days,
    error: firstBoardError ? serverSentence(firstBoardError.error) : null,
  });

  const shift = (n) => { if (start) { setWeek(addDaysISO(start, n * 7)); setPicked(null); } };
  const saved = () => { setMoving(null); qc.invalidateQueries({ queryKey: ['dispatch'] }); };

  // The week grid's chip. Its ROW names the crew member and its COLUMN names
  // the day, so the two facts a board card carries are already answered here —
  // what was missing is the same thing the two-day board was missing, the
  // STATUS. It drew one pill, for Blocked, so a job nobody has started and one
  // finished an hour ago were the same grey box. `jobCardBadges` is the same
  // vocabulary the cards below use, wrapped so three pills in a 120px column
  // stack instead of widening the grid.
  const chip = (job) => (
    <button key={job.id} type="button" className="dp-cw__chip" data-job-id={job.id} onClick={() => setMoving(job)}>
      <span className="dp-cw__chip-title">{job.title}</span>
      {chipMeta(job) ? <span className="dp-cw__chip-sub">{chipMeta(job)}</span> : null}
      <span className="dp-cw__chip-tags">
        {jobCardBadges(job).map((b) => <Pill key={b.key} tone={b.tone}>{b.label}</Pill>)}
      </span>
    </button>
  );

  // A cell we could not ask about. Never an empty box — that reads as "free".
  const unknownCell = <span className="dp-cw__unknown">Not loaded</span>;

  return (
    <>
      <div className="dp-nav">
        <FwButton variant="ghost" icon="chevronLeft" aria-label="Previous week" onClick={() => shift(-1)} disabled={!start} />
        <div className="dp-nav__label">{start ? `${weekOfLabel(start)} · ${dayLong(start)} – ${dayLong(days[days.length - 1])}` : ''}</div>
        <FwButton variant="ghost" icon="chevronRight" aria-label="Next week" onClick={() => shift(1)} disabled={!start} />
      </div>

      {roster.error ? (
        <Notice tone="stop" className="dp-notice" title="Could not load the crew">
          {serverSentence(roster.error, 'The week could not be loaded.')}
        </Notice>
      ) : null}
      {warning ? <Notice tone="warn" className="dp-notice">{warning}</Notice> : null}
      {loading ? <Skeleton lines={7} /> : null}

      {!loading && start && phone ? (
        <div className="dp-cw dp-cw--phone">
          {/*
            A week strip is one of the two places docs/APP_REBUILD.md §5 says a
            sideways drag IS the interaction, so it declares the exemption
            scripts/ui-check.js requires — visible here rather than hidden in a
            stylesheet, because an exemption a reviewer cannot see is how a
            check stops finding things.

            The arithmetic behind it, measured: seven day buttons at 67px come
            to 469px. Fitting them into a 320px phone's 288px of content would
            make each 41px — under the 48px minimum this app uses everywhere
            because crews wear gloves (--fw-tap). So the choice is a strip that
            pans or targets a gloved thumb misses, and panning wins.

            If these ever shrink below --fw-tap, the exemption is wrong, not the
            check.
          */}
          <div className="dp-cw__daypicker" role="tablist" aria-label="Days" data-scroll-x="ok">
            {days.map((d) => {
              const n = dayJobCount(rows, d);
              return (
                <button
                  key={d}
                  role="tab"
                  type="button"
                  aria-selected={activeDay === d}
                  className={`dp-cw__day-btn${activeDay === d ? ' is-active' : ''}${d === today ? ' is-today' : ''}`}
                  onClick={() => setPicked(d)}
                >
                  <span className="dp-cw__day-label">{dayShort(d)}</span>
                  <span className="dp-cw__day-count">{n === null ? '?' : n}</span>
                </button>
              );
            })}
          </div>
          {rows.map((r) => {
            const cell = r.days.find((d) => d.date === activeDay);
            const jobs = cell && cell.jobs;
            return (
              <div key={r.id} className="dp-cw__mrow" data-row={r.id}>
                <div className={`dp-cw__mrow-head${r.id === UNASSIGNED_ROW ? ' is-unassigned' : ''}`}>
                  <span className="dp-cw__name">{r.name}</span>
                  <span className="dp-cw__count">
                    {jobs === null ? 'Not loaded' : jobs.length === 0 ? 'Nothing on' : `${jobs.length} ${jobs.length === 1 ? 'job' : 'jobs'}`}
                  </span>
                </div>
                {jobs === null ? <div className="dp-cw__mrow-jobs">{unknownCell}</div> : null}
                {jobs && jobs.length > 0 ? <div className="dp-cw__mrow-jobs">{jobs.map(chip)}</div> : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {!loading && start && !phone ? (
        <div className="dp-cw dp-cw__scroll">
          <div className="dp-cw__grid" style={{ gridTemplateColumns: `minmax(120px, 0.8fr) repeat(${days.length}, minmax(120px, 1fr))` }}>
            <div className="dp-cw__corner">Crew</div>
            {days.map((d) => (
              <div key={d} className={`dp-cw__dayhead${d === today ? ' is-today' : ''}`}>
                <span className="dp-cw__wd">{dayShort(d)}</span>
                <span className="dp-cw__dn">{dayJobCount(rows, d) === null ? 'not loaded' : `${dayJobCount(rows, d)}`}</span>
              </div>
            ))}
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <div className={`dp-cw__rowhead${r.id === UNASSIGNED_ROW ? ' is-unassigned' : ''}`} data-row={r.id}>
                  <span className="dp-cw__name">{r.name}</span>
                  {r.free ? <span className="dp-cw__free">Free all week</span> : null}
                </div>
                {r.days.map((cell) => (
                  <div key={cell.date} className={`dp-cw__cell${cell.date === today ? ' is-today' : ''}${cell.unknown ? ' is-unknown' : ''}`}>
                    {cell.unknown ? unknownCell : cell.jobs.map(chip)}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : null}

      {moving ? (
        <MoveSheet
          job={moving}
          days={days.map((d) => ({ date: d }))}
          today={today}
          leads={((roster.data && roster.data.workers) || []).map((w) => ({ user_id: w.user_id, name: w.name }))}
          onClose={() => setMoving(null)}
          onSaved={saved}
        />
      ) : null}
    </>
  );
}

export default function Board() {
  const [view, setView] = useState('days');
  const [date, setDate] = useState(null);
  const [moving, setMoving] = useState(null);
  const phone = usePhoneLayout();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['dispatch', 'board', date],
    queryFn: () => get(boardApiPath(date)),
    enabled: view === 'days',
  });
  const data = q.data;
  const days = list(data && data.days);
  const first = (days[0] && days[0].date) || date;

  const leads = useMemo(() => {
    const seen = new Map();
    for (const d of days) for (const c of list(d.crews)) if (c.lead && !seen.has(c.lead.user_id)) seen.set(c.lead.user_id, c.lead);
    if (moving && moving.lead_user_id != null && !seen.has(moving.lead_user_id)) seen.set(moving.lead_user_id, { user_id: moving.lead_user_id, name: 'Current lead' });
    return [...seen.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [days, moving]);

  const shift = (n) => { if (first) setDate(addDaysISO(first, n)); };
  const saved = () => { setMoving(null); qc.invalidateQueries({ queryKey: ['dispatch'] }); };

  const week = view === 'week';
  // No title of our own. This screen is "Crew board" in the sidebar, in the
  // More sheet and in the tab bar, and it used to head itself "Board" — so a
  // manager tapped one name and arrived at another. shell/chrome.js says an
  // empty title means "use the destination's label from navigation.js", which
  // makes the menu the one place the name lives; rename the row and this
  // follows. Only the EYEBROW is ours, because the day is the one thing the
  // menu cannot know. DispatchScreenNames.test.js holds it.
  usePageChrome('', week ? 'This week' : (first ? dayLong(first) : ''));
  return (
    <>
      {/* The two board controls in ONE box, which is a measurement rather than
          a tidy-up: PhoneShell's body is a flex column with a 16px gap, so
          every top-level child of a screen pays that gap. The view switch and
          the day stepper were two children — 32px of the 314px of chrome that
          stood between a dispatcher and the first job card at 390px. They are
          one child now, with 4px between them. Neither control changed. */}
      <div className="dp-boardbar">
        <div className="dp-viewtabs" role="tablist" aria-label="Board view">
          <button type="button" role="tab" aria-selected={!week} className={`dp-viewtab${!week ? ' is-active' : ''}`} onClick={() => setView('days')}>
            Two days
          </button>
          <button type="button" role="tab" aria-selected={week} className={`dp-viewtab${week ? ' is-active' : ''}`} onClick={() => setView('week')}>
            Week by crew
          </button>
        </div>
        {week ? null : (
          <div className="dp-nav">
            <FwButton variant="ghost" icon="chevronLeft" aria-label="Previous day" onClick={() => shift(-1)} disabled={!first} />
            <div className="dp-nav__label">{first ? `${relativeDayLabel(first, data && data.today)} · ${dayLong(first)}` : ''}</div>
            <FwButton variant="ghost" icon="chevronRight" aria-label="Next day" onClick={() => shift(1)} disabled={!first} />
          </div>
        )}
      </div>
      {week ? <CrewWeek phone={phone} /> : null}
      {week ? null : (
      <>
      {q.isPending ? <Skeleton lines={6} /> : null}
      {q.error ? <Notice tone="stop" className="dp-notice">{describeError(q.error)}</Notice> : null}
      {data ? (
        <div className={phone ? 'dp-days' : 'dp-days dp-days--wide'}>
          {days.map((d) => {
            const crews = list(d.crews);
            const unassigned = list(d.unassigned);
            // Computed once per day and handed down, so the summary and the
            // cards cannot disagree about whether the day is all one state.
            const all = dayJobs(d);
            const sameStatusAllDay = dayStatusIsUniform(all);
            const summary = daySummary(all);
            return (
              <section key={d.date} className="dp-day" data-date={d.date}>
                {/* The heading and the day's own size on ONE row. The summary
                    carries what the cards stopped repeating (the shared
                    status) plus what only the day can say — how much work,
                    how much of it moving, how much behind — and it costs no
                    vertical room, which is the other half of this change:
                    38% of a 390px screen was chrome before the first card. */}
                <div className="dp-day__head">
                  <h2 className="dp-day__title">{relativeDayLabel(d.date, data.today)}</h2>
                  {summary ? <span className="dp-day__sum">{summary}</span> : null}
                </div>
                {crews.length === 0 && unassigned.length === 0 ? (
                  <EmptyState title="Nothing scheduled">No jobs on {dayLong(d.date)}.</EmptyState>
                ) : null}
                {crews.map((c) => (
                  <div key={c.lead.user_id} className="dp-crew" data-lead={c.lead.user_id}>
                    <SectionLabel>
                      {c.lead.name}
                      {list(c.members).length ? ` · ${c.members.map((m) => m.name).join(', ')}` : ''}
                    </SectionLabel>
                    <JobList jobs={list(c.jobs)} day={d.date} onPick={setMoving} leadName={c.lead.name} sameStatusAllDay={sameStatusAllDay} />
                  </div>
                ))}
                {/*
                  Work with nobody on it is the one group on this screen a
                  dispatcher is here to empty, and it read exactly like a crew:
                  one SectionLabel, same weight, same colour, at the bottom of
                  the day. It now says how many, in the same words the card
                  badges use, so the size of the problem is on the heading
                  rather than counted by hand.
                */}
                {unassigned.length ? (
                  <div className="dp-crew dp-crew--unassigned" data-lead="unassigned">
                    <SectionLabel>
                      Unassigned · {unassigned.length} {unassigned.length === 1 ? 'job' : 'jobs'}
                    </SectionLabel>
                    <JobList jobs={unassigned} day={d.date} onPick={setMoving} sameStatusAllDay={sameStatusAllDay} />
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      ) : null}
      {moving ? (
        <MoveSheet job={moving} days={days} today={data && data.today} leads={leads} onClose={() => setMoving(null)} onSaved={saved} />
      ) : null}
      </>
      )}
    </>
  );
}
